import { canUsePreset } from '#~/control/preset/access.js';
import { DeviceRegistryCommandKey } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { pbNativeTargetToJs, selectStoredPreset } from '#~/control/preset/native-owner.js';
import * as Commands from '#~/control/preset/commands/index.js';
import { isCalibrating } from '@studio/models/control/calibration';
import { locateProxy } from '#~/agent/utils.js';
import type { Opaque } from 'type-fest';
import { ElectronAPI } from '#~/electron/api.js';
import type { Device } from '@studio/models/device';
import type { Preset } from '@studio/models/control/preset';
import { logger } from '#~/logger/index.js';

const RECALL = DeviceRegistryCommandKey.PRESET_RECALL;
const SET = DeviceRegistryCommandKey.PRESET_SET;
/**
 * Handle preset-related commands
 * @returns true if a preset command was handled
 */
export function handlePresetCommand(
  key: DeviceRegistryCommandKey,
  values: number[],
  devID: Device['id']
): boolean {
  // Check if the command is a preset command.
  if (key !== RECALL && key !== SET) {
    return false;
  }
  if (values.length !== 1) {
    logger.warning('>> Invalid preset command');
    return true;
  }
  if (!canUsePreset(devID)) return true;
  const presetNum = values[0]!;
  if (key === RECALL) {
    selectPreset(presetNum, devID);
  } else if (key === SET) {
    setPreset(presetNum, devID);
  }
  return true;
}

function setPreset(_presetNum: number, deviceID: Device['id']) {
  // For now the preset number doesn't matter since we always add to the list
  // of presets.
  const devID = deviceID as Opaque<string, '__deviceId'>;
  // Get groups, preset and calibrating status from state.
  const proxy = locateProxy(devID);
  if (!proxy) {
    logger.warning('>> setPreset: No proxy found');
    return;
  }
  const groups = proxy.state.control?.studio?.device[devID]?.control?.presets?.groups;
  const presets = proxy.state.control?.studio?.device[devID]?.control?.presets?.items;
  const calibrating = isCalibrating(proxy.state.status?.device[devID]?.calibration?.status?.state);

  if (!groups) {
    return;
  }
  if (calibrating) {
    return;
  }

  // Get the first group id.
  // TODO: Handle multiple groups. For now, we are assuming only one group.
  const groupKeys = Object.keys(groups).sort();
  const groupID = groupKeys[0] as Opaque<string, '__positionPresetGroupId'>;
  if (!groupID) {
    return;
  }
  // Get the new preset rank based on the number of presets in the group.
  const newRank = Object.values(presets ?? {}).filter((p) => p.group === groupID).length;
  const num = presets ? Object.keys(presets).length + 1 : 1;

  ElectronAPI.getAccessToken()
    .then((token) => {
      const preset = {
        name: `Preset ${num}`,
        rank: newRank
      };
      Commands.addPreset(devID, groupID, preset, token).catch((err) => {
        logger.error('>> Error adding preset:', err);
      });
    })
    .catch((err) => {
      logger.error('>> Error adding preset:', err);
    });
}

function selectPreset(presetNum: number, devID: Device['id']) {
  const proxy = locateProxy(devID);
  if (!proxy) {
    logger.warning('>> selectPreset: No proxy found');
    return;
  }
  const presets = proxy.state.control?.studio?.device[devID]?.control?.presets?.items;
  if (!presets) {
    return;
  }
  const presetID = Object.keys(presets ?? {}).find((key) => presets[key]?.rank === presetNum - 1);
  if (!presetID) {
    return;
  }

  const preset = presets[presetID];
  if (!preset) {
    return;
  }
  // Build the model-typed Preset the owner expects; ids are opaque wrappers over
  // the wire strings, everything else maps one-to-one from the stored record.
  const jsPreset: Preset = {
    id: presetID as Preset['id'],
    groupId: preset.group as Preset['groupId'],
    name: preset.name ?? 'Preset',
    rank: preset.rank ?? 0,
    thumbnail: preset.thumbnail,
    requiredAxes: preset.requiredAxes,
    nativeTarget: pbNativeTargetToJs(preset.nativeTarget),
    position: preset.position
      ? {
          pan: preset.position.pan ?? 0,
          tilt: preset.position.tilt ?? 0,
          zoom: preset.position.zoom ?? 0
        }
      : undefined
  };
  selectStoredPreset(devID, jsPreset).catch((err) => {
    logger.error('>> Error moving to preset:', err);
  });
}
